import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getWorkHours, deleteWorkHour, batchDeleteWorkHours, createWorkHour, updateWorkHour } from '../../../services/workHourService';

const ColorCircle = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#ccc'};
`;

const useWorkHourApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getWorkHours(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await createWorkHour(formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await updateWorkHour(id, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await deleteWorkHour(ids[0]);
      } else {
        await batchDeleteWorkHours(ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const formatTime = useCallback((v) => {
    if (!v) return '-';
    if (typeof v === 'string' && v.includes(':')) {
      const parts = v.split(':');
      return `${parts[0]}:${parts[1]}`;
    }
    return v;
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('workHour.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'shortCode', label: t('workHour.shortCode'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'colorCode', label: t('workHour.colorCode'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v ? <ColorCircle $color={v} /> : '-',
      },
    },
    {
      name: 'start', label: t('workHour.start'),
      options: {
        filter: true, sort: true,
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => formatTime(v),
      },
    },
    {
      name: 'end', label: t('workHour.end'),
      options: {
        filter: true, sort: true,
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => formatTime(v),
      },
    },
    { name: 'breakCount', label: t('workHour.breakCount'), options: { filter: false, sort: false } },
  ], [t, formatTime]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('workHour.name'), type: 'text', grid: 12, required: true },
    { name: 'shortCode', label: t('workHour.shortCode'), type: 'text', grid: 6 },
    { name: 'colorCode', label: t('workHour.colorCode'), type: 'colorPicker', grid: 6 },
    { name: 'start', label: t('workHour.start'), type: 'time', grid: 6, required: true },
    { name: 'end', label: t('workHour.end'), type: 'time', grid: 6, required: true },
    { name: 'lateEntranceTolerance', label: t('workHour.lateEntranceTolerance'), type: 'number', grid: 6 },
    { name: 'earlyLeaveTolerance', label: t('workHour.earlyLeaveTolerance'), type: 'number', grid: 6 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useWorkHourApi;
